/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

import { getFileIconPreview, hasFileIconPreview } from 'utils/file-icon';
import { getAccessToken } from 'utils/access-token';

const StyledIconWrapper = styled('div')(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  '& .MuiSvgIcon-root': {
    fontSize: '50px !important',
    width: '50px !important',
    height: '50px !important',
  },
}));

export const AuthenticatedImage = ({ file, previewId, alt, onLoadSuccess, onLoadError }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [detectedType, setDetectedType] = useState(null);

  // eslint-disable-next-line no-undef
  const baseURL = process.env.REACT_APP_API_AGENTSPACE_SERVICE;
  const accessToken = getAccessToken();

  useEffect(() => {
    if (!previewId || !accessToken) {
      setLoading(false);
      setError(true);
      return;
    }

    let objectUrl;
    const fetchImage = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch(`${baseURL}/api/files/${previewId}/download?redirect=content`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'cache-control': 'max-age=3600',
          },
        });

        if (!response.ok) {
          throw new Error('Image fetch failed');
        }

        const imageBlob = await response.blob();
        if (!imageBlob.type.startsWith('image/')) {
          setDetectedType(imageBlob.type);
          setLoading(false);
          return;
        }
        objectUrl = URL.createObjectURL(imageBlob);
        setImageSrc(objectUrl);
        if (onLoadSuccess) onLoadSuccess(file);
      } catch (error) {
        setError(true);
        setImageSrc(null);
        if (onLoadError) onLoadError(file);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [previewId, accessToken, onLoadSuccess, onLoadError]);

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{ width: '100%', height: '100%' }}
      />
    );
  }

  if (!imageSrc || error) {
    const fileWithCorrectType = {
      ...file,
      content_type: file?.content_type || detectedType,
    };

    return (
      <StyledIconWrapper>
        {hasFileIconPreview(fileWithCorrectType) ? getFileIconPreview(fileWithCorrectType) : getFileIconPreview({})}
      </StyledIconWrapper>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt || 'File preview'}
      onError={() => setError(true)}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
    />
  );
};
